import {
    type ClaimLinkDialogState,
    type ClaimLinkResponse,
    createClaimLinkDialogState,
    type OrgMember,
    type OrgMemberListItem,
    type OrgMemberRole,
    type OrgUserStatus
} from "@solomon-apps/shared";
import { getInvitationExpiryState } from "@solomon-apps/shared/src/utils/invitationExpiry";

import { asClientFormError } from "@/utils/clientFormErrors";

/** A Team mutation failure as the console words it. */
export function asTeamError(error: unknown): Error {
    return asClientFormError(error, "team", "Team update was not saved. Check the member details and try again.");
}

export interface RoleSpec {
    label: string;
    /** One-line capability statement, no terminal period. */
    headline: string;
}

export const ROLE_ORDER: OrgMemberRole[] = ["Owner", "Member"];

/**
 * Account access. The client console has no permissions to grant yet, so this
 * axis carries the whole model here: a client organization neither builds
 * Programs nor decides distributions, which is why Program Manager and
 * Distribution Approver are not offered on this side.
 *
 * The consequence is that every client write, onboarding included, is
 * Owner-gated, so expect client orgs to carry more than one Owner. That is the
 * pressure that eventually justifies a client onboarding permission.
 *
 * The role is always shown by its name ("Owner" / "Member"), in the table and
 * in the detail alike. The headline explains it where the role is chosen.
 */
export const ROLE_DESCRIPTIONS: Record<OrgMemberRole, RoleSpec> = {
    Owner: {
        label: "Owner",
        headline: "Manages people and completes onboarding"
    },
    Member: {
        label: "Member",
        // Not the issuer console's "needs a permission": there are none to
        // grant on this side, so a client Member is not waiting on a grant,
        // they are waiting on an Owner.
        headline: "Views everything; Owners make changes"
    }
};

/**
 * Owner-issued password reset only applies to Active members: Invited
 * members use their claim link instead, and Suspended members must be
 * reactivated first (mirrors the backend INVALID_STATE guard).
 */
export function canResetMemberPassword(member: Pick<OrgMember, "status">): boolean {
    return member.status === "Active";
}

export async function requestMemberPasswordReset(
    memberUuid: string,
    resetPassword: (uuid: string) => Promise<ClaimLinkResponse>
): Promise<ClaimLinkDialogState> {
    const result = await resetPassword(memberUuid);
    return createClaimLinkDialogState(result, "password-reset");
}

const DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
});

export function formatLastLogin(iso: string | null, now: number = Date.now()): string {
    if (!iso) return "Never";
    const date = new Date(iso);
    const diffMs = now - date.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    if (diffHours < 24) {
        const hours = Math.floor(diffHours);
        return hours <= 0 ? "just now" : `${hours}h ago`;
    }
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return DATE_FORMAT.format(date);
}

/* ── Identity ─────────────────────────────────────────────────── */

export function displayName(member: Pick<OrgMember, "fullName" | "email">): string {
    return member.fullName || member.email;
}

/**
 * The short name a narrow Team table shows: the first word of the full name.
 * An invite with no name keeps its email, as `displayName` does.
 */
export function firstName(member: Pick<OrgMember, "fullName" | "email">): string {
    const first = member.fullName?.trim().split(/\s+/)[0];
    return first || member.email;
}

/* ── Status ───────────────────────────────────────────────────── */

export type StatusTone = "success" | "pending" | "disabled";

/**
 * The status a row states, in Title Case. An Invited member whose link has
 * lapsed reads "Invite Expired": still Invited on the server, but nobody can
 * claim it until an Owner resends.
 */
export function memberStatusLabel(
    member: Pick<OrgMemberListItem, "status" | "inviteExpiresAt">,
    now: number = Date.now()
): string {
    if (member.status === "Invited" && getInvitationExpiryState(member.inviteExpiresAt, now) === "expired") {
        return "Invite Expired";
    }
    return member.status;
}

const STATUS_TONE: Record<OrgUserStatus, StatusTone> = {
    Active: "success",
    Invited: "pending",
    Suspended: "disabled"
};

export function memberStatusTone(
    member: Pick<OrgMemberListItem, "status" | "inviteExpiresAt">,
    now: number = Date.now()
): StatusTone {
    if (member.status === "Invited" && getInvitationExpiryState(member.inviteExpiresAt, now) === "expired") {
        return "disabled";
    }
    return STATUS_TONE[member.status];
}

/** Status tone → the label's text colour (as the Programs status badge). */
export const STATUS_TONE_TEXT: Record<StatusTone, string> = {
    success: "text-success-base",
    pending: "text-warning-base",
    disabled: "text-foreground-muted"
};

/**
 * The Last Active cell. Signed-in members show their last login (relative
 * inside a week, then a date); an invite has no login yet, so it shows when
 * its link lapses instead.
 */
export function formatLastActive(
    member: Pick<OrgMemberListItem, "status" | "lastLoginAt" | "inviteExpiresAt">,
    now: number = Date.now()
): string {
    if (member.status === "Invited") {
        const state = getInvitationExpiryState(member.inviteExpiresAt, now);
        if (state === "unknown" || !member.inviteExpiresAt) return "Never";
        const date = DATE_FORMAT.format(new Date(member.inviteExpiresAt));
        return state === "expired" ? `Invite expired ${date}` : `Invite expires ${date}`;
    }
    const value = formatLastLogin(member.lastLoginAt, now);
    return value.charAt(0).toUpperCase() + value.slice(1);
}

export function formatAdded(iso: string): string {
    const time = Date.parse(iso);
    return Number.isFinite(time) ? DATE_FORMAT.format(new Date(time)) : "";
}

/* ── Filter, search, count ────────────────────────────────────── */

export type TeamStatusFilter = "All" | OrgUserStatus;

export const STATUS_FILTERS: TeamStatusFilter[] = ["All", "Active", "Invited", "Suspended"];

export function countByStatus(members: Pick<OrgMember, "status">[]): Record<TeamStatusFilter, number> {
    const counts: Record<TeamStatusFilter, number> = {
        All: members.length,
        Active: 0,
        Invited: 0,
        Suspended: 0
    };
    for (const member of members) counts[member.status] += 1;
    return counts;
}

/** Case-insensitive substring match on name or email; blank matches all. */
export function matchesQuery(member: Pick<OrgMember, "fullName" | "email">, query: string): boolean {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return member.email.toLowerCase().includes(needle) || (member.fullName ?? "").toLowerCase().includes(needle);
}

export function filterMembers<T extends Pick<OrgMember, "fullName" | "email" | "status">>(
    members: T[],
    { status, query }: { status: TeamStatusFilter; query: string }
): T[] {
    return members.filter((member) => (status === "All" || member.status === status) && matchesQuery(member, query));
}

/* ── Sort ─────────────────────────────────────────────────────── */

/** `default` is the exception-first order; the rest are column sorts. */
export type TeamSortColumn = "default" | "name" | "role" | "lastActive";
export type TeamSortDirection = "asc" | "desc";

export interface TeamSort {
    column: TeamSortColumn;
    direction: TeamSortDirection;
}

export const DEFAULT_TEAM_SORT: TeamSort = { column: "default", direction: "asc" };

const STATUS_RANK: Record<OrgUserStatus, number> = {
    Invited: 0,
    Suspended: 1,
    Active: 2
};

function loginTime(member: Pick<OrgMember, "lastLoginAt">): number {
    const time = member.lastLoginAt ? Date.parse(member.lastLoginAt) : Number.NaN;
    return Number.isFinite(time) ? time : Number.NEGATIVE_INFINITY;
}

/**
 * Exception-first by default (Invited, then Suspended, then Active, with
 * yourself last among the Active), alphabetical within each group. A column
 * sort replaces that order; name breaks every tie so the order is stable.
 * Last Active ascending runs oldest first, with never-signed-in at the top.
 */
export function sortMembers<T extends OrgMember>(members: T[], sort: TeamSort, selfUuid: string | undefined): T[] {
    const byName = (a: T, b: T): number => displayName(a).localeCompare(displayName(b));
    const sign = sort.direction === "asc" ? 1 : -1;
    return [...members].sort((a, b) => {
        switch (sort.column) {
            case "default": {
                const byStatus = STATUS_RANK[a.status] - STATUS_RANK[b.status];
                if (byStatus !== 0) return byStatus;
                if (a.status === "Active" && a.uuid !== b.uuid) {
                    if (a.uuid === selfUuid) return 1;
                    if (b.uuid === selfUuid) return -1;
                }
                return byName(a, b);
            }
            case "name":
                return sign * byName(a, b);
            case "role": {
                const byRole = ROLE_ORDER.indexOf(a.orgRole) - ROLE_ORDER.indexOf(b.orgRole);
                return byRole !== 0 ? sign * byRole : byName(a, b);
            }
            case "lastActive": {
                const ta = loginTime(a);
                const tb = loginTime(b);
                if (ta !== tb) return sign * (ta < tb ? -1 : 1);
                return byName(a, b);
            }
        }
    });
}

/**
 * Next sort after a header click: a new column starts in its natural
 * direction (Last Active most recent first, the rest A to Z); the same column
 * flips.
 */
export function nextTeamSort(current: TeamSort, column: Exclude<TeamSortColumn, "default">): TeamSort {
    if (current.column === column) {
        return { column, direction: current.direction === "asc" ? "desc" : "asc" };
    }
    return { column, direction: column === "lastActive" ? "desc" : "asc" };
}

/* ── Ownership continuity ─────────────────────────────────────── */

export const LAST_OWNER_REASON = "An organization needs at least one Owner. Make another member an Owner first.";

export function activeOwnerCount(members: Pick<OrgMember, "orgRole" | "status">[]): number {
    return members.filter((m) => m.orgRole === "Owner" && m.status === "Active").length;
}

/**
 * True for the one Active Owner an org cannot lose: demoting or suspending
 * them would leave nobody able to manage the team (the backend refuses it
 * with LAST_ADMIN; this lets the UI say so before the attempt).
 */
export function isLastActiveOwner(
    member: Pick<OrgMember, "orgRole" | "status">,
    members: Pick<OrgMember, "orgRole" | "status">[]
): boolean {
    return member.orgRole === "Owner" && member.status === "Active" && activeOwnerCount(members) <= 1;
}

export interface MemberGuardContext {
    /** The row is the signed-in user. */
    isSelf: boolean;
    /** The signed-in user is an Owner (only Owners manage the team). */
    viewerIsOwner: boolean;
    /** The row is the org's only Active Owner. */
    isLastOwner: boolean;
}

export const OWNERS_ONLY_REASON = "Only Owners can change roles and access.";
const SELF_ROLE_REASON = "Another Owner can change your role.";
const SELF_SUSPEND_REASON = "You can't suspend yourself.";
const SELF_RESET_REASON = "Change your own password from Profile.";

/**
 * Why the role control is locked for this member, or null when it is
 * editable. The last-Owner rule wins over the self rule because it is the
 * one that tells you what to do next.
 */
export function roleChangeLockReason(ctx: MemberGuardContext): string | null {
    if (!ctx.viewerIsOwner) return OWNERS_ONLY_REASON;
    if (ctx.isLastOwner) return LAST_OWNER_REASON;
    if (ctx.isSelf) return SELF_ROLE_REASON;
    return null;
}

/** Why Suspend is unavailable for this member, or null. */
export function suspendLockReason(ctx: MemberGuardContext): string | null {
    if (!ctx.viewerIsOwner) return OWNERS_ONLY_REASON;
    if (ctx.isSelf) return SELF_SUSPEND_REASON;
    if (ctx.isLastOwner) return LAST_OWNER_REASON;
    return null;
}

/** Why Reset Password is unavailable for this member, or null. */
export function resetPasswordLockReason(ctx: MemberGuardContext): string | null {
    if (!ctx.viewerIsOwner) return OWNERS_ONLY_REASON;
    if (ctx.isSelf) return SELF_RESET_REASON;
    return null;
}

export const SUSPENDED_RESET_REASON = "Reactivate this member to reset their password.";

/**
 * Why the member page's Send Reset Link is unavailable, or null. A Suspended
 * member has to be reactivated first (the backend's INVALID_STATE); the
 * viewer and self rules follow `resetPasswordLockReason`.
 */
export function memberResetLockReason(member: Pick<OrgMember, "status">, ctx: MemberGuardContext): string | null {
    return resetPasswordLockReason(ctx) ?? (canResetMemberPassword(member) ? null : SUSPENDED_RESET_REASON);
}

/**
 * Why Suspend / Reactivate is unavailable, or null. Reactivating is never the
 * risky direction, so it needs only an Owner viewer.
 */
export function statusToggleLockReason(member: Pick<OrgMember, "status">, ctx: MemberGuardContext): string | null {
    if (member.status === "Suspended") return ctx.viewerIsOwner ? null : OWNERS_ONLY_REASON;
    return suspendLockReason(ctx);
}

/**
 * Who may edit a member's name: an Owner, or the member themselves (the
 * backend lets anyone rename their own account).
 */
export function canEditMemberName(ctx: Pick<MemberGuardContext, "isSelf" | "viewerIsOwner">): boolean {
    return ctx.viewerIsOwner || ctx.isSelf;
}

/** The Team page's route. */
export const TEAM_PATH = "/team";

/**
 * The Add Member page's route. Registered before `/team/:memberUuid`, so
 * "new" never reads as a member uuid.
 */
export const TEAM_NEW_PATH = "/team/new";

/** The member page's route. */
export function teamMemberPath(uuid: string): string {
    return `/team/${encodeURIComponent(uuid)}`;
}

/* ── Row actions ──────────────────────────────────────────────── */

export type MemberActionKind =
    | "changeRole"
    | "resetPassword"
    | "suspend"
    | "reactivate"
    | "resendInvitation"
    | "editInvitation"
    | "withdrawInvitation";

export interface MemberAction {
    kind: MemberActionKind;
    label: string;
    destructive: boolean;
    /** Shown with the disabled item; null when the action is available. */
    disabledReason: string | null;
}

/**
 * The row's ⋯ menu, by status. Owners only: a Member manages nobody, so they
 * get no menu. Actions on yourself stay listed but disabled with the reason,
 * so the menu's shape does not change from row to row.
 */
export function memberActions(member: Pick<OrgMember, "status">, ctx: MemberGuardContext): MemberAction[] {
    if (!ctx.viewerIsOwner) return [];
    switch (member.status) {
        case "Invited":
            return [
                { kind: "resendInvitation", label: "Resend Invitation", destructive: false, disabledReason: null },
                { kind: "editInvitation", label: "Edit Invitation", destructive: false, disabledReason: null },
                { kind: "withdrawInvitation", label: "Withdraw Invitation", destructive: true, disabledReason: null }
            ];
        case "Suspended":
            return [{ kind: "reactivate", label: "Reactivate", destructive: false, disabledReason: null }];
        case "Active":
            return [
                {
                    kind: "changeRole",
                    label: "Change Role",
                    destructive: false,
                    disabledReason: roleChangeLockReason(ctx)
                },
                {
                    kind: "resetPassword",
                    label: "Reset Password",
                    destructive: false,
                    disabledReason: resetPasswordLockReason(ctx)
                },
                {
                    kind: "suspend",
                    label: "Suspend",
                    destructive: true,
                    disabledReason: suspendLockReason(ctx)
                }
            ];
    }
}

/* ── Invite ───────────────────────────────────────────────────── */

// Pragmatic format check (non-empty local part @ domain . tld), mirroring the
// issuer console's invite check. The server validates again.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string): boolean {
    return EMAIL_PATTERN.test(value.trim());
}

/**
 * Why an invite address cannot go yet, or null when it can. Read on demand
 * (the Send Invite button's tooltip), never as a sentence under the
 * field. An address already on the team is held back here rather than sent
 * to fail with EMAIL_IN_USE (case-insensitive).
 */
export function inviteEmailProblem(raw: string, existingEmails: Iterable<string> = []): string | null {
    const email = raw.trim();
    if (!email) return "Required";
    if (!isValidEmail(email)) return "Invalid Email";
    const key = email.toLowerCase();
    for (const existing of existingEmails) {
        if (existing.trim().toLowerCase() === key) return "Already Added";
    }
    return null;
}

/**
 * Router state Add Member hands the new member's page: the claim link from
 * the invite response, which no later read returns.
 */
export interface NewInvitationState {
    invitation: ClaimLinkResponse;
}

/** The invitation Add Member handed over, or null for any other visit. */
export function readNewInvitation(state: unknown): ClaimLinkResponse | null {
    if (typeof state !== "object" || state === null || !("invitation" in state)) return null;
    const invitation = (state as { invitation: unknown }).invitation;
    if (typeof invitation !== "object" || invitation === null) return null;
    const { claimUrl, delivery } = invitation as Partial<ClaimLinkResponse>;
    if (typeof claimUrl !== "string" || !claimUrl) return null;
    if (delivery !== "email_and_url" && delivery !== "url") return null;
    return { claimUrl, delivery };
}
